/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isNonNegativeInteger from '@stdlib/assert-is-nonnegative-integer';
import isUnityProbabilityArray from '@stdlib/assert-is-unity-probability-array';
import hasOwnProp from '@stdlib/assert-has-own-property';
import { isPrimitive as isBoolean } from '@stdlib/assert-is-boolean';
import isObject from '@stdlib/assert-is-plain-object';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Validates function options.
*
* @private
* @param {Object} opts - destination for validated options
* @param {Options} options - function options
* @param {NonNegativeInteger} [options.size] - sample size
* @param {ProbabilityArray} [options.probs] - element probabilities
* @param {boolean} [options.replace] - boolean indicating whether to sample with replacement
* @param {boolean} [options.mutate] - boolean indicating whether to mutate the `pool` when sampling without replacement
* @returns {(null|Error)} null or an error
*
* @example
* var opts = {};
* var options = {
*     'size': 10,
*     'replace': false,
*     'mutate': true,
*     'probs': [ 0.7, 0.3 ]
* };
* var err = validate( opts, options );
* if ( err ) {
*     throw err;
* }
*/
function validate( opts, options ) {
	if ( !isObject( options ) ) {
		return new TypeError( format( '0qd2V', options ) );
	}
	if ( hasOwnProp( options, 'size' ) ) {
		opts.size = options.size;
		if ( !isNonNegativeInteger( opts.size ) ) {
			return new TypeError( format( '0qd2t', 'size', opts.size ) );
		}
	}
	if ( hasOwnProp( options, 'probs' ) ) {
		opts.probs = options.probs;
		if ( !isUnityProbabilityArray( opts.probs ) ) {
			return new TypeError( format( '0qd7l', 'probs', opts.probs ) );
		}
	}
	if ( hasOwnProp( options, 'mutate' ) ) {
		opts.mutate = options.mutate;
		if ( !isBoolean( opts.mutate ) ) {
			return new TypeError( format( '0qd2o', 'mutate', opts.mutate ) );
		}
	}
	if ( hasOwnProp( options, 'replace' ) ) {
		opts.replace = options.replace;
		if ( !isBoolean( opts.replace ) ) {
			return new TypeError( format( '0qd2o', 'replace', opts.replace ) );
		}
	}
	return null;
}


// EXPORTS //

export default validate;
